import { Mail, MapPin, Clock, Building2, HeartHandshake, ArrowUpRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PrimaryLink } from "../components/Bits";
import { CONTACT, IMAGES, REGIONS } from "../lib/site";

const ROUTES = [
  { icon: Building2, t: "Landlords", d: "Rent-to-rent enquiries and property offers.", to: "/landlords", cta: "For landlords" },
  { icon: HeartHandshake, t: "Local authorities", d: "Supported housing partnerships and placement enquiries.", to: "/local-authorities", cta: "For councils" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="relative pt-40 pb-16 lg:pt-52 overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img src={IMAGES.skylineDay} alt="" className="h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-ink/45" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent to-ink" />
          <div className="absolute -top-32 right-0 h-[34rem] w-[34rem] rounded-full bg-amber/10 blur-[120px]" />
        </div>
        <div className="container-x relative z-10">
          <Reveal><Overline>Get in touch</Overline></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-7xl text-white">
              Let's talk<br />housing.
            </h1>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Whether you're a landlord after a hands-off let or a council seeking quality supported
              housing, our North West team would love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Reveal className="lg:col-span-1">
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-card"
              className="group card-surface h-full rounded-3xl p-8 flex flex-col hover:border-amber/40 transition-colors"
            >
              <Mail className="h-8 w-8 text-amber" />
              <div className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Email us</div>
              <div className="mt-2 font-display font-bold text-xl text-white break-words group-hover:text-amber transition-colors">
                {CONTACT.email}
              </div>
              <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm text-amber">
                Send an email <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-1">
            <div data-testid="contact-response-card" className="card-surface h-full rounded-3xl p-8 flex flex-col">
              <Clock className="h-8 w-8 text-amber" />
              <div className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Fast response</div>
              <div className="mt-2 font-display font-bold text-xl text-white">Reply within one working day</div>
              <p className="mt-3 text-sm text-slate-400">Send us the details of your property or placement need and a member of our North West team will get straight back to you.</p>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                <Clock className="h-4 w-4 text-brandcyan" /> {CONTACT.hours}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-1">
            <div data-testid="contact-areas-card" className="card-surface h-full rounded-3xl p-8 flex flex-col">
              <MapPin className="h-8 w-8 text-amber" />
              <div className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Areas we cover</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {REGIONS.slice(0, 8).map((r) => (
                  <span key={r} className="rounded-full border border-white/12 px-3 py-1 text-xs text-slate-300">{r}</span>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-400">…and right across the wider North West of England.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="container-x pb-24">
        <Reveal><Overline>Point me in the right direction</Overline></Reveal>
        <Stagger className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {ROUTES.map((r, i) => (
            <StaggerItem key={i}>
              <div className="card-surface h-full rounded-3xl p-8 lg:p-10 flex flex-col hover:border-amber/30 transition-colors" data-testid={`contact-route-${i}`}>
                <r.icon className="h-9 w-9 text-amber" />
                <h3 className="mt-5 font-display font-bold text-2xl text-white">{r.t}</h3>
                <p className="mt-3 text-slate-400">{r.d}</p>
                <div className="mt-6"><PrimaryLink to={r.to} testid={`contact-route-cta-${i}`}>{r.cta}</PrimaryLink></div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="container-x pb-28 lg:pb-40">
        <Reveal>
          <div className="rounded-[2rem] card-surface p-10 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-amber/15 blur-[100px]" />
            <div className="relative">
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white">Prefer to just email?</h2>
              <p className="mt-4 text-slate-300">Drop us a line and a member of our team will get back to you within one working day.</p>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-bottom-email"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber px-7 py-4 text-sm font-semibold text-ink hover:bg-amber-soft transition-colors"
              >
                <Mail className="h-4 w-4" /> {CONTACT.email}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
